<template>
  <BModal v-model="showModalInternal" :title="isCreate ? 'Nowa rola' : 'Edytuj rolę'" hide-footer scrollable>
    <div v-if="isRoleAdmin" class="alert alert-warning d-flex align-items-center mb-3">
      <i class="fas fa-lock me-2"></i>
      <span>ROLE_ADMIN jest rolą systemową i nie może być edytowana.</span>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-primary fw-bold">
          <i class="fas fa-user-tag me-2"></i>Dane roli
        </h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label required">Nazwa</label>
            <input
              type="text"
              class="form-control"
              v-model="form.name"
              :disabled="isRoleAdmin"
              placeholder="Wprowadź nazwę roli"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Opis</label>
            <textarea
              class="form-control"
              v-model="form.description"
              rows="3"
              :disabled="isRoleAdmin"
              placeholder="Opis roli (opcjonalnie)"
            ></textarea>
          </div>
          <div class="col-12">
            <label class="form-label required">Grupa</label>
            <BFormSelect v-model="form.roleGroup" :options="roleGroupOptions" :disabled="isRoleAdmin" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isCreate" class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-secondary fw-bold">
          <i class="fas fa-shield-alt me-2"></i>Uprawnienia (opcjonalnie)
        </h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Dostępne uprawnienia</label>
            <BFormSelect
              v-model="selectedPrivilegeKeys"
              :options="privilegesOptions"
              multiple
              size="6"
              :disabled="isRoleAdmin"
            />
            <small class="text-muted mt-1 d-block">Wybierz uprawnienia, które mają być przypisane do roli.</small>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-secondary" @click="showModalInternal = false">Anuluj</button>
      <button
        type="button"
        class="btn btn-primary"
        @click="saveRole"
        :disabled="isRoleAdmin || !form.name || !form.roleGroup"
      >
        <i class="fas fa-save me-1"></i> Zapisz
      </button>
    </div>
  </BModal>
</template>

<script>
import { BModal, BFormSelect } from "bootstrap-vue-next";
import { useUserRbacManagerModule } from "@/state/pinia/userRbacManagerModule";

export default {
  name: "RoleFormModal",
  components: { BModal, BFormSelect },
  props: {
    showModal: { type: Boolean, required: true },
    role: { type: Object, default: () => ({}) },
    privilegesCatalog: { type: Array, default: () => [] },
  },
  emits: ["update:showModal", "onSaved"],
  data() {
    return {
      showModalInternal: this.showModal,
      form: {
        name: "",
        description: "",
        roleGroup: "ROLE_USER",
      },
      selectedPrivilegeKeys: [],
      roleGroupOptions: [
        { value: "ROLE_USER", text: "ROLE_USER" },
        { value: "ROLE_MODERATOR", text: "ROLE_MODERATOR" },
        { value: "ROLE_ADMIN", text: "ROLE_ADMIN" },
      ],
    };
  },
  computed: {
    rbacModule() {
      return useUserRbacManagerModule();
    },
    isCreate() {
      return !this.role || !this.resolveRoleId(this.role);
    },
    isRoleAdmin() {
      return !this.isCreate && this.role && this.role.name === "ROLE_ADMIN";
    },
    privilegesOptions() {
      return this.privilegesCatalog.map((privilege) => ({
        value: privilege.key || privilege.privilegeKey || privilege.name,
        text: `${privilege.key || privilege.privilegeKey || privilege.name}`,
      }));
    },
  },
  watch: {
    showModal(newValue) {
      this.showModalInternal = newValue;
      if (newValue) {
        this.resetForm();
      }
    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
  },
  methods: {
    resolveRoleId(role) {
      return role.id || role.roleId || role.guid;
    },
    resetForm() {
      this.form = {
        name: this.role?.name || "",
        description: this.role?.description || "",
        roleGroup: this.role?.roleGroup || "ROLE_USER",
      };
      const privilegeKeys = this.role?.privilegeKeys || this.role?.privileges || [];
      this.selectedPrivilegeKeys = (Array.isArray(privilegeKeys) ? privilegeKeys : []).map((item) =>
        typeof item === "string" ? item : item.key || item.privilegeKey || item.name
      );
    },
    saveRole() {
      if (this.isRoleAdmin) return;
      const payload = {
        name: this.form.name,
        description: this.form.description,
        roleGroup: this.form.roleGroup,
      };
      if (this.isCreate) {
        payload.privilegeKeys = this.selectedPrivilegeKeys;
        this.rbacModule.httpCreateRole(payload).then(() => {
          this.$emit("onSaved");
          this.showModalInternal = false;
        });
        return;
      }

      this.rbacModule
        .httpUpdateRole({
          id: this.resolveRoleId(this.role),
          data: payload,
        })
        .then(() => {
          this.$emit("onSaved");
          this.showModalInternal = false;
        });
    },
  },
};
</script>

<style scoped>
.required:after {
  content: " *";
  color: red;
}
</style>
